/**
 * native-viewport — the SDK's own, un-faked view of the viewport (FLO-523).
 *
 * `squeeze-compat`'s viewport facade PATCHES `window.innerWidth` and
 * `window.matchMedia` so an uncooperative host page believes the viewport is
 * `real − dockWidth` while the rail is docked. That lie is for the HOST. The SDK
 * must never read it, and the reason is not tidiness — it is a feedback loop:
 *
 *   real 1440 → facade reports 1040 → `useViewportTier` sees `(min-width: 1024px)`
 *   fail at 1440−400 → tier drops to 'overlay' → the squeeze effect releases →
 *   undocked, so the facade reports 1440 again → tier back to 'squeeze' → squeeze
 *   re-applied → …
 *
 * i.e. the rail would flap the host page open/closed forever at any width within
 * one dock-width of a breakpoint. So every SDK read of a width-sensitive viewport
 * API goes through this module, which hands back the ORIGINAL function/getter the
 * facade displaced.
 *
 * Resolution order is deliberately "saved original first, live global second":
 *
 *   - facade active  → the pristine reference it captured when it patched, so it
 *     does not matter whether this module was imported before or after the patch
 *     (a module-eval snapshot would be order-dependent — and wrong in the test
 *     env, where `window` is stubbed long after import).
 *   - facade absent  → plain `window.matchMedia` / `window.innerWidth`. Flags off
 *     therefore means "call exactly what the caller used to call", which is why
 *     re-pointing the hooks at this module is a no-op for every existing site.
 *
 * Nothing else may write here: `squeeze-compat` is the only registrar, and it
 * clears its registration on teardown.
 */

/** The pristine references, published by squeeze-compat while its facade is up. */
interface NativeViewport {
  /** The real `window.matchMedia`, already bound to `window`. */
  matchMedia?: (query: string) => MediaQueryList;
  /** Reads the real `window.innerWidth` (through the displaced descriptor). */
  innerWidth?: () => number;
}

let native: NativeViewport | null = null;

/**
 * Publish the originals. Called by squeeze-compat AT PATCH TIME (before the
 * facade's own accessors go in), so a read racing the patch still resolves to
 * something real.
 */
export function setNativeViewport(refs: NativeViewport): void {
  native = { ...native, ...refs };
}

/** Drop the registration — the facade has restored the real globals. */
export function clearNativeViewport(): void {
  native = null;
}

/**
 * True when a `matchMedia` is callable at all. Mirrors the guard the hooks used
 * to inline (`typeof window.matchMedia !== 'function'`) so their SSR / node-env
 * degradation is unchanged.
 */
export function hasMatchMedia(): boolean {
  if (native?.matchMedia) return true;
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function';
}

/**
 * `window.matchMedia`, guaranteed un-patched. Returns null in the same cases the
 * old inline guard bailed on (no `window`, no `matchMedia`) — callers keep their
 * existing fallback branch.
 */
export function realMatchMedia(query: string): MediaQueryList | null {
  if (native?.matchMedia) return native.matchMedia(query);
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
  return window.matchMedia(query);
}

/**
 * `window.innerWidth`, guaranteed un-patched. 0 with no `window` (SSR/node) —
 * the same "no viewport" sentinel `viewportTier()` already treats as desktop.
 */
export function realInnerWidth(): number {
  if (native?.innerWidth) return native.innerWidth();
  if (typeof window === 'undefined') return 0;
  const w = window.innerWidth;
  return typeof w === 'number' && Number.isFinite(w) ? w : 0;
}
